/**
 * Placement context editing form component.
 * Used for updating context metadata on existing placements.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import './placement-form.scss';

const contextSchema = z.object({
  platform: z.string().optional(),
  placement_type: z.string().optional(),
  post_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  author_handle: z.string().optional(),
  author: z.string().optional(),
  excerpt: z.string().optional(),
});

export type PlacementContextFormData = z.infer<typeof contextSchema>;

export interface PlacementContextFormProps {
  initialContext?: Record<string, unknown> | null;
  onSubmit: (context: Record<string, unknown>) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export function PlacementContextForm({
  initialContext,
  onSubmit,
  onCancel,
  isLoading = false,
  error,
}: PlacementContextFormProps) {
  // Extract context fields from initialContext
  const defaultValues: Partial<PlacementContextFormData> = {
    platform: (initialContext?.platform as string) || '',
    placement_type: (initialContext?.placement_type as string) || '',
    post_url: (initialContext?.post_url as string) || '',
    author_handle: (initialContext?.author_handle as string) || '',
    author: (initialContext?.author as string) || '',
    excerpt: (initialContext?.excerpt as string) || (initialContext?.context_excerpt as string) || '',
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PlacementContextFormData>({
    resolver: zodResolver(contextSchema),
    defaultValues,
  });

  // Transform form data to include context object before submission
  const handleFormSubmit = async (data: PlacementContextFormData) => {
    // Build context object from form fields (only include non-empty values)
    const context: Record<string, unknown> = {};
    if (data.platform && data.platform.trim()) context.platform = data.platform.trim();
    if (data.placement_type && data.placement_type.trim()) context.placement_type = data.placement_type.trim();
    if (data.post_url && data.post_url.trim()) context.post_url = data.post_url.trim();
    if (data.author_handle && data.author_handle.trim()) context.author_handle = data.author_handle.trim();
    if (data.author && data.author.trim()) context.author = data.author.trim();
    if (data.excerpt && data.excerpt.trim()) context.excerpt = data.excerpt.trim();

    // Merge with existing context to preserve any additional fields
    const finalContext = {
      ...(initialContext || {}),
      ...context,
    };

    // Submit the context object
    await onSubmit(finalContext);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="placement-form">
      {error && (
        <div className="placement-form__error-message" style={{ 
          padding: 'var(--of-space-3)', 
          marginBottom: 'var(--of-space-4)', 
          backgroundColor: 'var(--of-color-error-light, #fee)', 
          color: 'var(--of-color-error, #c33)', 
          borderRadius: 'var(--of-radius-sm, 4px)',
          border: '1px solid var(--of-color-error, #c33)'
        }}>
          {error}
        </div>
      )}

      <div className="placement-form__field">
        <label htmlFor="platform" className="label">
          Platform
        </label>
        <select
          id="platform"
          {...register('platform')}
          className="select"
          disabled={isLoading}
        >
          <option value="">Select platform</option>
          <option value="facebook.com">Facebook</option>
          <option value="x.com">X (Twitter)</option>
          <option value="linkedin.com">LinkedIn</option>
          <option value="reddit.com">Reddit</option>
          <option value="youtube.com">YouTube</option>
          <option value="instagram.com">Instagram</option>
          <option value="tiktok.com">TikTok</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="placement-form__field">
        <label htmlFor="placement_type" className="label">
          Placement Type
        </label>
        <select
          id="placement_type"
          {...register('placement_type')}
          className="select"
          disabled={isLoading}
        >
          <option value="">Select type</option>
          <option value="post">Post</option>
          <option value="comment">Comment</option>
          <option value="article">Article</option>
          <option value="email">Email</option>
          <option value="ad">Ad</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="placement-form__field">
        <label htmlFor="post_url" className="label">
          Post/Content URL
        </label>
        <input
          id="post_url"
          type="url"
          {...register('post_url')}
          className="input"
          placeholder="https://facebook.com/user/posts/12345"
          disabled={isLoading}
        />
        <p className="placement-form__help-text">
          URL of the post/article where this link is placed
        </p>
        {errors.post_url && (
          <p className="placement-form__error">{errors.post_url.message}</p>
        )}
      </div>

      <div className="placement-form__field">
        <label htmlFor="author_handle" className="label">
          Author Handle
        </label>
        <input
          id="author_handle"
          type="text"
          {...register('author_handle')}
          className="input"
          placeholder="@username or u/username"
          disabled={isLoading}
        />
      </div>

      <div className="placement-form__field">
        <label htmlFor="author" className="label">
          Author Name
        </label>
        <input
          id="author"
          type="text"
          {...register('author')}
          className="input"
          placeholder="John Doe"
          disabled={isLoading}
        />
      </div>

      <div className="placement-form__field">
        <label htmlFor="excerpt" className="label">
          Excerpt/Notes
        </label>
        <textarea
          id="excerpt"
          {...register('excerpt')}
          className="input"
          placeholder="Brief description or excerpt from the post..."
          rows={3}
          disabled={isLoading}
        />
      </div>

      <div className="placement-form__actions">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Context'}
        </Button>
      </div>
    </form>
  );
}

